/**
 * 防抖函数 Debounce
 *
 * @example
 *   const debouncedFetchGames = debounce(fetchGames, 300);
 *
 * @param fn 函数
 * @param delay 延迟时间
 */

export function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  fn: F,
  delay: number,
): (...args: Parameters<F>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<F>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

/**
 * 节流函数 Throttle
 *
 * @example
 *   const throttledFn = throttle(fetchGames, 300);
 *
 * @param fn 要执行的函数
 * @param delay 节流时间间隔（毫秒）
 */

export function throttle<F extends (...args: Parameters<F>) => ReturnType<F>>(
  fn: F,
  delay: number,
): (...args: Parameters<F>) => void {
  let lastCall = 0;
  return (...args: Parameters<F>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}

/** 防重复管理器 用于防止在指定时间间隔内重复执行相同的操作 当前仅用于toast.error，解决相同errorCode错误短时间内重复显示toast */
export class DuplicatePreventionManager<T = number> {
  private timeMap = new Map<T, number>();
  private defaultInterval: number; // 默认的防重复时间间隔
  private maxSize: number; // 最大记录数
  private lastCleanup: number = 0; // 上次清理过期记录的时间戳
  private cleanupInterval: number = 60000; // 1分钟清理一次

  constructor(defaultInterval: number = 5000, maxSize: number = 100) {
    this.defaultInterval = defaultInterval;
    this.maxSize = maxSize;
  }

  shouldExecute(key: T, interval?: number): boolean {
    this.autoCleanup(); // 自动清理过期记录

    const now = Date.now();
    const lastTime = this.timeMap.get(key) ?? 0;
    const checkInterval = interval ?? this.defaultInterval;

    if (now - lastTime < checkInterval) {
      return false;
    }

    this.timeMap.set(key, now);
    return true;
  }

  private autoCleanup(): void {
    const now = Date.now();

    // 定期清理或超过最大大小时清理
    if (
      now - this.lastCleanup > this.cleanupInterval ||
      this.timeMap.size > this.maxSize
    ) {
      this.cleanupExpiredEntries(now);
      this.lastCleanup = now;
    }
  }

  private cleanupExpiredEntries(now: number): void {
    for (const [key, timestamp] of this.timeMap.entries()) {
      if (now - timestamp > this.defaultInterval * 2) {
        // 清理超过2倍间隔的记录
        this.timeMap.delete(key);
      }
    }
  }
}
